import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Phone, Mail, MapPin, FileText, Tag, X, Search, CheckCircle } from 'lucide-react';
import CustomerDatePicker from '../CustomerDatePicker';

const AdminNewAppointment = ({
  services,
  appointments,
  coupons,
  bundles = [],
  blockedDates = [],
  customers,
  // A customer record the caller already picked (the Book button on a customer
  // card). Prefills exactly as a successful lookup would.
  initialCustomer = null,
  createAppointment,
  adminSettings,
  onClose,
  onSuccess
}) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    // The office has no email for this customer. Explicit, so an empty field is
    // still a mistake rather than a silent no-email booking.
    noEmail: false,
    address: '',
    city: '',
    state: '',
    zip: '',
    selectedServiceIds: [],
    date: '',
    time: '',
    notes: '',
    couponCode: '',
    notificationPreference: 'email',
    acUnitHeight: '',
    acUnitWidth: '',
    sendNotification: true
  });

  const [detectedBundle, setDetectedBundle] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lookupValue, setLookupValue] = useState('');
  const [lookupStatus, setLookupStatus] = useState(null);
  const [lookupName, setLookupName] = useState('');
  const [lookupResults, setLookupResults] = useState([]);

  const isACCoveringService = () => {
    if (formData.selectedServiceIds.length === 0) return false;
    return formData.selectedServiceIds.some(sid => {
      const svc = services.find(s => s.id.toString() === sid.toString());
      return svc && (
        svc.name.toLowerCase().includes('ac covering') ||
        svc.name.toLowerCase().includes('ac-covering')
      );
    });
  };

  const handleServiceToggle = (serviceId) => {
    const sid = serviceId.toString();
    setFormData(prev => {
      const current = prev.selectedServiceIds.map(String);
      const updated = current.includes(sid)
        ? current.filter(id => id !== sid)
        : [...current, sid];
      return { ...prev, selectedServiceIds: updated, date: '', time: '' };
    });
    setAvailableTimes([]);
  };

  // Bundle auto-detection
  useEffect(() => {
    if (formData.selectedServiceIds.length < 2 || !bundles || bundles.length === 0) {
      setDetectedBundle(null);
      return;
    }
    const selectedSet = new Set(formData.selectedServiceIds.map(String));
    const match = bundles.find(bundle => {
      if (!bundle.active) return false;
      const bundleSet = new Set(bundle.service_ids.map(String));
      if (bundleSet.size !== selectedSet.size) return false;
      for (const id of bundleSet) {
        if (!selectedSet.has(id)) return false;
      }
      return true;
    });
    setDetectedBundle(match || null);
    if (match) {
      setAppliedCoupon(null);
      setCouponError('');
    }
  }, [formData.selectedServiceIds, bundles]);

  const getSelectedServices = () => {
    return formData.selectedServiceIds
      .map(sid => services.find(s => s.id.toString() === sid.toString()))
      .filter(Boolean);
  };

  const calculatePricing = () => {
    const selected = getSelectedServices();
    const subtotal = selected.reduce((sum, s) => sum + parseFloat(s.price), 0);
    const totalDuration = selected.reduce((sum, s) => sum + (parseInt(s.duration) || 45), 0);
    let bundleDiscount = 0;
    let priceAfterBundle = subtotal;

    if (detectedBundle) {
      priceAfterBundle = parseFloat(detectedBundle.bundle_price);
      bundleDiscount = subtotal - priceAfterBundle;
    }

    let couponDiscount = 0;
    if (appliedCoupon && !detectedBundle) {
      if (appliedCoupon.discount_type === 'percentage') {
        couponDiscount = priceAfterBundle * (appliedCoupon.discount_value / 100);
      } else {
        couponDiscount = appliedCoupon.discount_value;
      }
    }

    const finalPrice = Math.max(0, priceAfterBundle - couponDiscount);
    return { subtotal, bundleDiscount, priceAfterBundle, couponDiscount, finalPrice, totalDuration, selected };
  };

  // Load available times when date or services change
  useEffect(() => {
    if (formData.date && formData.selectedServiceIds.length > 0) {
      loadAvailableTimes(formData.date, formData.selectedServiceIds);
    }
  }, [formData.date, formData.selectedServiceIds]);

  const loadAvailableTimes = async (date, serviceIds) => {
    try {
      const serviceParam = serviceIds.length > 1
        ? `service_ids=${serviceIds.join(',')}`
        : `service_id=${serviceIds[0]}`;
      const response = await fetch(`/scheduler/api/availability.php?date=${date}&${serviceParam}`);
      const result = await response.json();
      if (result.success) {
        const times = result.data || [];
        const normalizedTimes = times.map(timeItem => {
          if (typeof timeItem === 'string') return timeItem;
          if (typeof timeItem === 'object' && timeItem.value) return timeItem.value;
          if (typeof timeItem === 'object' && timeItem.time) return timeItem.time;
          return null;
        }).filter(time => time !== null);
        setAvailableTimes(normalizedTimes);
      } else {
        setAvailableTimes([]);
      }
    } catch (error) {
      console.error('Error loading available times:', error);
      setAvailableTimes([]);
    }
  };

  const formatPhoneNumber = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)})${numbers.slice(3)}`;
    if (numbers.length <= 10) return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6)}`;
    return `(${numbers.slice(0, 3)})${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const fillFromResult = (source, data) => {
    if (source === 'customers') {
      const d = data;
      let address = d.address_line1 || d.address || '';
      let city = d.city || '';
      let state = d.state || '';
      let zip = d.zip || '';

      // If customer record has no address, check their most recent appointment
      if (!address) {
        const custEmail = (d.email || '').toLowerCase().trim();
        const custPhone = (d.phone || '').replace(/\D/g, '');
        const pastApt = [...appointments].reverse().find(a => {
          const aEmail = (a.customer_email || '').toLowerCase().trim();
          const aPhone = (a.customer_phone || '').replace(/\D/g, '');
          return (custEmail && aEmail === custEmail) || (custPhone && aPhone === custPhone);
        });
        if (pastApt && pastApt.service_address) {
          const parts = pastApt.service_address.split(',').map(s => s.trim());
          address = parts[0] || '';
          city = parts[1] || city;
          if (parts[2]) {
            const stateZip = parts[2].trim().split(/\s+/);
            state = stateZip[0] || state;
            zip = stateZip[1] || zip;
          }
        }
      }

      setFormData(prev => ({
        ...prev,
        firstName: d.first_name || '',
        lastName: d.last_name || '',
        email: d.email || '',
        // A customer record saved without an email carries the flag forward, so
        // booking them does not stall on a field that will never be filled.
        noEmail: !d.email,
        sendNotification: !!d.email && prev.sendNotification,
        phone: d.phone ? formatPhoneNumber(d.phone) : '',
        address,
        city,
        state,
        zip,
        acUnitHeight: d.ac_unit_height ? String(d.ac_unit_height) : '',
        acUnitWidth: d.ac_unit_width ? String(d.ac_unit_width) : ''
      }));
      setLookupName(`${d.first_name} ${d.last_name}`);
    } else {
      const d = data;
      const nameParts = (d.customer_name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      let address = '', city = '', state = '', zip = '';
      if (d.service_address) {
        const parts = d.service_address.split(',').map(s => s.trim());
        address = parts[0] || '';
        city = parts[1] || '';
        if (parts[2]) {
          const stateZip = parts[2].trim().split(/\s+/);
          state = stateZip[0] || '';
          zip = stateZip[1] || '';
        }
      }
      setFormData(prev => ({
        ...prev,
        firstName,
        lastName,
        email: d.customer_email || '',
        noEmail: !d.customer_email,
        sendNotification: !!d.customer_email && prev.sendNotification,
        phone: d.customer_phone ? formatPhoneNumber(d.customer_phone) : '',
        address,
        city,
        state,
        zip
      }));
      setLookupName(`${firstName} ${lastName}`);
    }
  };

  // Prefill from a customer handed in by the caller. Runs once per customer:
  // re-running would stomp edits the office has already made to the form.
  useEffect(() => {
    if (!initialCustomer) return;
    fillFromResult('customers', initialCustomer);
    setLookupStatus('found');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCustomer]);

  const selectLookupResult = (result) => {
    fillFromResult(result.source, result.data);
    setLookupResults([]);
    setLookupStatus('found');
  };

  const handleLookup = async () => {
    if (!lookupValue.trim()) return;
    setLookupStatus('loading');
    setLookupResults([]);
    try {
      const response = await fetch(`/scheduler/api/customers.php?lookup=${encodeURIComponent(lookupValue.trim())}`);
      const result = await response.json();
      if (result.success && result.found) {
        if (result.multiple) {
          setLookupResults(result.results);
          setLookupStatus('multiple');
        } else {
          fillFromResult(result.source, result.data);
          setLookupStatus('found');
        }
      } else {
        setLookupStatus('not_found');
      }
    } catch (error) {
      console.error('Customer lookup error:', error);
      setLookupStatus('not_found');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (name === 'date') {
      setFormData(prev => ({ ...prev, time: '' }));
    }
  };

  const handleCouponApply = () => {
    const coupon = coupons.find(c =>
      c.code.toLowerCase() === formData.couponCode.toLowerCase() &&
      c.active &&
      new Date(c.expiry_date || c.expiryDate) > new Date()
    );
    if (coupon) {
      if (coupon.service_id && !formData.selectedServiceIds.includes(String(coupon.service_id)) && !formData.selectedServiceIds.includes(coupon.service_id)) {
        const serviceName = services.find(s => s.id === coupon.service_id || s.id === String(coupon.service_id))?.name || 'a specific service';
        setAppliedCoupon(null);
        setCouponError(`This coupon is only valid for ${serviceName}`);
        return;
      }
      const normalizedCoupon = {
        ...coupon,
        discount_type: coupon.type,
        discount_value: coupon.value
      };
      setAppliedCoupon(normalizedCoupon);
      setCouponError('');
    } else {
      setAppliedCoupon(null);
      setCouponError('Invalid or expired coupon code');
    }
  };

  const sendEmailNotification = async (appointmentData, type) => {
    try {
      await fetch('/scheduler/api/send-email.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ appointmentData, type })
      });
    } catch (error) {
      console.error(`Error sending ${type} email:`, error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const selectedServices = getSelectedServices();
    if (selectedServices.length === 0) {
      alert('Please select at least one service');
      setIsSubmitting(false);
      return;
    }

    if (isACCoveringService()) {
      if (!formData.acUnitHeight || !formData.acUnitWidth) {
        alert('Please enter both height and width measurements for the AC unit.');
        setIsSubmitting(false);
        return;
      }
    }

    const pricing = calculatePricing();
    const isMulti = selectedServices.length > 1;

    const appointmentData = {
      customer_name: `${formData.firstName} ${formData.lastName}`,
      customer_phone: formData.phone,
      customer_email: formData.noEmail ? '' : formData.email,
      no_email: formData.noEmail,
      service_address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`,
      ...(isMulti
        ? { service_ids: formData.selectedServiceIds.map(Number) }
        : {
            service_id: formData.selectedServiceIds[0],
            service_name: selectedServices[0].name,
            service_price: parseFloat(selectedServices[0].price)
          }
      ),
      appointment_date: formData.date,
      appointment_time: formData.time,
      final_price: pricing.finalPrice,
      coupon_code: appliedCoupon ? appliedCoupon.code : null,
      discount_amount: pricing.couponDiscount + pricing.bundleDiscount,
      bundle_id: detectedBundle ? detectedBundle.id : null,
      bundle_name: detectedBundle ? detectedBundle.name : null,
      bundle_discount: pricing.bundleDiscount,
      notes: formData.notes,
      notification_preference: 'email',
      status: 'scheduled',
      ac_unit_height: formData.acUnitHeight || null,
      ac_unit_width: formData.acUnitWidth || null
    };

    const result = await createAppointment(appointmentData);

    if (result && result.success) {
      // Send email notifications if enabled. The admin copy is skipped too when
      // there is no customer address - it echoes the customer mail and would
      // report a confirmation that never went out.
      if (formData.sendNotification && !formData.noEmail) {
        await sendEmailNotification(result.data, 'customer');
        await sendEmailNotification(result.data, 'admin');
      }

      setShowSuccess(true);
      setTimeout(() => {
        if (onSuccess) onSuccess();
        onClose();
      }, 2000);
    } else {
      alert('Failed to create appointment: ' + (result?.error || 'Unknown error'));
      setIsSubmitting(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string' || !timeStr.includes(':')) return timeStr || '';
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      if (isNaN(hour)) return timeStr;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return String(timeStr);
    }
  };

  // Success screen
  if (showSuccess) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-8">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Appointment Created!</h2>
          <p className="text-gray-600">The appointment has been successfully scheduled.</p>
          {formData.sendNotification && (
            <p className="text-sm text-gray-500 mt-2">Confirmation emails have been sent.</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-green-800">New Appointment</h2>
          <p className="text-gray-600">
            {initialCustomer
              ? `Booking for ${[initialCustomer.first_name, initialCustomer.last_name].filter(Boolean).join(' ') || 'this customer'}`
              : 'Fill out the form below to book a service'}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <X className="w-6 h-6 text-gray-500" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-2 gap-8">
        {/* Left Column - Customer Info */}
        <div className="space-y-6">
          {/* Customer Lookup */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Returning Customer?</h3>
            <p className="text-xs text-gray-500 mb-3">Enter phone number or email to auto-fill details.</p>
            <div className="flex gap-2">
              <input
                type="text"
                value={lookupValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^[\d(]/.test(val) && !val.includes('@')) {
                    setLookupValue(formatPhoneNumber(val));
                  } else {
                    setLookupValue(val);
                  }
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleLookup(); } }}
                placeholder="Phone or email"
                className="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <button
                type="button"
                onClick={handleLookup}
                disabled={lookupStatus === 'loading' || !lookupValue.trim()}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg transition-colors text-sm flex items-center gap-1"
              >
                <Search className="w-4 h-4" />
                {lookupStatus === 'loading' ? 'Looking up...' : 'Look Up'}
              </button>
            </div>
            {lookupStatus === 'found' && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                Found: <strong>{lookupName}</strong> — details filled in below.
              </div>
            )}
            {lookupStatus === 'multiple' && lookupResults.length > 0 && (
              <div className="mt-3">
                <p className="text-sm text-gray-700 mb-2">Multiple accounts found. Select one:</p>
                <div className="space-y-2">
                  {lookupResults.map((result, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => selectLookupResult(result)}
                      className="w-full text-left p-3 bg-white border border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 transition-colors"
                    >
                      <span className="font-medium text-gray-800">{result.name}</span>
                      <span className="text-gray-500 text-sm ml-2">{result.email}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {lookupStatus === 'not_found' && (
              <div className="mt-3 p-3 bg-gray-100 border border-gray-200 rounded text-sm text-gray-600">
                No account found — fill in details below.
              </div>
            )}
          </div>

          {/* Name Fields */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                First Name *
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Phone Number *
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Email {!formData.noEmail && '*'}
              </label>
              <input
                type="email"
                name="email"
                value={formData.noEmail ? '' : formData.email}
                onChange={handleInputChange}
                required={!formData.noEmail}
                disabled={formData.noEmail}
                placeholder={formData.noEmail ? 'No email on file' : ''}
                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 disabled:bg-gray-100"
              />
              <label className="flex items-center gap-2 mt-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={formData.noEmail}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    noEmail: e.target.checked,
                    email: e.target.checked ? '' : prev.email,
                    sendNotification: e.target.checked ? false : prev.sendNotification
                  }))}
                />
                No email address for this customer
              </label>
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <MapPin className="w-4 h-4 inline mr-1" />
              Service Address *
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              placeholder="Street Address"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500 mb-3"
            />
            <div className="grid grid-cols-3 gap-3">
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleInputChange}
                required
                placeholder="City"
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <input
                type="text"
                name="state"
                value={formData.state}
                onChange={handleInputChange}
                required
                placeholder="State"
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <input
                type="text"
                name="zip"
                value={formData.zip}
                onChange={handleInputChange}
                required
                placeholder="ZIP"
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Additional Notes (Optional)
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Any specific details or requirements..."
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Send Notification Toggle */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.sendNotification && !formData.noEmail}
                disabled={formData.noEmail}
                onChange={(e) => setFormData(prev => ({ ...prev, sendNotification: e.target.checked }))}
                className="mr-3 text-blue-600 focus:ring-blue-500 rounded"
              />
              <div>
                <span className="font-medium text-blue-800">Send confirmation email to customer</span>
                <p className="text-xs text-blue-600 mt-1">
                  {formData.noEmail
                    ? 'No email address on file - nothing will be sent.'
                    : 'Uncheck to skip email notification for this appointment'}
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Right Column - Service & Scheduling */}
        <div className="space-y-6">
          {/* Service Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Service(s) *</label>
            <p className="text-xs text-gray-500 mb-3">Select one or more services. Bundle discounts apply automatically!</p>
            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {services.filter(s => s.active !== false).map(service => {
                const isSelected = formData.selectedServiceIds.map(String).includes(service.id.toString());
                return (
                  <label
                    key={service.id}
                    className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-green-50 border-2 border-green-400'
                        : 'bg-gray-50 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleServiceToggle(service.id)}
                      disabled={isSubmitting}
                      className="mt-1 text-green-600 focus:ring-green-500 rounded"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <span className="font-medium text-gray-800">{service.name}</span>
                        <span className="font-bold text-green-600">${parseFloat(service.price).toFixed(2)}</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {service.duration || 45} min
                        {service.description && ` - ${service.description}`}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            {formData.selectedServiceIds.length === 0 && (
              <p className="text-xs text-red-500 mt-1">Please select at least one service</p>
            )}
          </div>

          {/* Bundle Detection */}
          {detectedBundle && (
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-400 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-bold text-yellow-800">Bundle Deal Applied!</h4>
              </div>
              <p className="text-sm text-yellow-700">
                <strong>{detectedBundle.name}</strong> — Save ${parseFloat(detectedBundle.savings || 0).toFixed(2)}!
              </p>
            </div>
          )}

          {/* AC Unit Measurements */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-blue-800 mb-3 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              AC Unit Measurements
            </h3>
            <p className="text-xs text-blue-600 mb-3">
              Measure the outdoor AC unit (required for AC covering services).
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Height (inches) {isACCoveringService() && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  name="acUnitHeight"
                  value={formData.acUnitHeight}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 30"
                  min="12"
                  max="60"
                  required={isACCoveringService()}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1">Typical range: 24-36 inches</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Width (inches) {isACCoveringService() && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="number"
                  name="acUnitWidth"
                  value={formData.acUnitWidth}
                  onChange={handleInputChange}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., 30"
                  min="18"
                  max="48"
                  required={isACCoveringService()}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-gray-500 mt-1">Typical range: 24-36 inches</p>
              </div>
            </div>
          </div>

          {/* Date Selection */}
          {formData.selectedServiceIds.length > 0 ? (
            <CustomerDatePicker
              selectedDate={formData.date}
              onDateSelect={(dateStr) => {
                setFormData(prev => ({ ...prev, date: dateStr, time: '' }));
                setAvailableTimes([]);
              }}
              blockedDates={blockedDates.filter(bd => bd.is_entire_date_blocked)}
              serviceIds={formData.selectedServiceIds}
              isSubmitting={isSubmitting}
            />
          ) : (
            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Select Date *</h3>
              <p className="text-sm text-gray-500">Please select a service first to see available dates.</p>
            </div>
          )}

          {/* Time Selection */}
          {availableTimes.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Clock className="w-4 h-4 inline mr-1" />
                Available Times *
              </label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                {availableTimes.map((time, index) => (
                  <label key={`${time}-${index}`} className="flex items-center">
                    <input
                      type="radio"
                      name="time"
                      value={time}
                      checked={formData.time === time}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    <span className="text-sm">{formatTime(time)}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Coupon Code */}
          <div>
            {detectedBundle ? (
              <p className="text-sm text-gray-500 italic">
                <Tag className="w-4 h-4 inline mr-1" />
                Coupon codes cannot be combined with bundle deals.
              </p>
            ) : (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Tag className="w-4 h-4 inline mr-1" />
                  Coupon Code (Optional)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    name="couponCode"
                    value={formData.couponCode}
                    onChange={handleInputChange}
                    placeholder="Enter coupon code"
                    className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <button
                    type="button"
                    onClick={handleCouponApply}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Apply
                  </button>
                </div>
                {couponError && <p className="text-red-600 text-sm mt-1">{couponError}</p>}
                {appliedCoupon && (
                  <p className="text-green-600 text-sm mt-1">
                    Coupon applied: {appliedCoupon.discount_value}
                    {appliedCoupon.discount_type === 'percentage' ? '%' : '$'} off
                  </p>
                )}
              </>
            )}
          </div>

          {/* Price Summary */}
          {formData.selectedServiceIds.length > 0 && (
            <div className="bg-green-50 rounded-lg p-4">
              <h3 className="font-semibold text-green-800 mb-2">Price Summary</h3>
              {(() => {
                const pricing = calculatePricing();
                return (
                  <div className="space-y-1 text-sm">
                    {pricing.selected.map(svc => (
                      <div key={svc.id} className="flex justify-between">
                        <span>{svc.name} ({svc.duration || 45} min)</span>
                        <span>${parseFloat(svc.price).toFixed(2)}</span>
                      </div>
                    ))}
                    {pricing.selected.length > 1 && (
                      <div className="flex justify-between pt-1 border-t border-green-200">
                        <span>Subtotal ({pricing.totalDuration} min):</span>
                        <span>${pricing.subtotal.toFixed(2)}</span>
                      </div>
                    )}
                    {detectedBundle && pricing.bundleDiscount > 0 && (
                      <div className="flex justify-between text-yellow-700 font-medium">
                        <span>Bundle Deal ({detectedBundle.name}):</span>
                        <span>-${pricing.bundleDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    {appliedCoupon && pricing.couponDiscount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Coupon ({appliedCoupon.code}):</span>
                        <span>-${pricing.couponDiscount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-green-800 pt-1 border-t border-green-300">
                      <span>Total:</span>
                      <span>${pricing.finalPrice.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || formData.selectedServiceIds.length === 0 || !formData.date || !formData.time}
              className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Scheduling...' : 'Schedule Appointment'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AdminNewAppointment;
